import * as React from 'react';
import { ISeasonResortLiftTickets } from '@/api/resort/types';
import CurrencyNotice from '@/components/common/CurrencyNotice';
import { DataTable } from '@/components/common/DataTable';
import { I18nKey } from '@/i18n/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { formatDateFromStr } from '@/util/formatDate';
import { ticketsTypes, TicketsTypesEnum } from './constants';

// Season pass pricing table. The API returns ONE record per resort, so
// the body renders a single row — the leftmost cell shows
// "<season> · expires DD" and the remaining cells show the pass price
// per active age group. Age columns with no price are hidden.

const ticketLabelKey: Record<TicketsTypesEnum, string> = {
  [TicketsTypesEnum.children]: 'child',
  [TicketsTypesEnum.teenager]: 'junior',
  [TicketsTypesEnum.adult]: 'adult',
  [TicketsTypesEnum.senior]: 'senior',
};

interface IProps {
  seasonLiftTickets: ISeasonResortLiftTickets;
}

// Narrowed lookup — the `resort` field on ISeasonResortLiftTickets shares
// the type's keyof union, but constructed price/age keys always point at a
// string (or null), so cast the read site accordingly.
const readField = (s: ISeasonResortLiftTickets, key: keyof ISeasonResortLiftTickets) => (
  s[key] as string | null
);

export const hasSeasonPasses = (s: ISeasonResortLiftTickets) => ticketsTypes
  .some((age) => !!readField(s, `${age}Price` as keyof ISeasonResortLiftTickets));

export const SeasonPassesTable = ({ seasonLiftTickets }: IProps) => {
  const { t } = useOTSTranslation('common');

  const activeAges = ticketsTypes.filter(
    (age) => !!readField(seasonLiftTickets, `${age}Price` as keyof ISeasonResortLiftTickets),
  );

  if (!activeAges.length) return null;

  return (
    <>
      {seasonLiftTickets.currency && (
        <CurrencyNotice className="mb-3" currency={seasonLiftTickets.currency} />
      )}
      <DataTable minWidth={560}>
        <DataTable.Head>
          <DataTable.HeadRow>
            <DataTable.HeadCell>{t('resort.liftTickets.validSeason')}</DataTable.HeadCell>
            {activeAges.map((age) => {
              const labelKey = ticketLabelKey[age];
              const ageRange = readField(seasonLiftTickets, `${age}Age` as keyof ISeasonResortLiftTickets);
              return (
                <DataTable.HeadCell key={age} align="right">
                  <span className="block">{t(`resort.liftTickets.${labelKey}` as I18nKey['common'])}</span>
                  {ageRange ? (
                    <span className="block text-xs font-normal normal-case opacity-70">{ageRange}</span>
                  ) : null}
                </DataTable.HeadCell>
              );
            })}
          </DataTable.HeadRow>
        </DataTable.Head>
        <DataTable.Body>
          <DataTable.Row>
            <DataTable.RowHeader>
              <span className="block">{seasonLiftTickets.season}</span>
              {seasonLiftTickets.expires && (
                <span className="block text-xs font-normal text-muted-foreground">
                  {`${t('resort.liftTickets.expirationDate')} ${formatDateFromStr(seasonLiftTickets.expires, 'DD')}`}
                </span>
              )}
            </DataTable.RowHeader>
            {activeAges.map((age) => (
              <DataTable.Cell key={age} align="right">
                {readField(seasonLiftTickets, `${age}Price` as keyof ISeasonResortLiftTickets) || t('common.dash')}
              </DataTable.Cell>
            ))}
          </DataTable.Row>
        </DataTable.Body>
      </DataTable>
    </>
  );
};
